import { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { careerHighlights, culturalProgrammes, leadershipInitiatives, leadershipRoles } from "../data/initiatives";

const TABS = [
  { key: "all", label: "All" },
  { key: "clinical", label: "Career" },
  { key: "community", label: "Programmes & Leadership" },
  { key: "leadership", label: "Research & Innovation" },
];

export default function Initiatives() {
  const [filter, setFilter] = useState("all");
  const showClinical = filter === "all" || filter === "clinical";
  const showCommunity = filter === "all" || filter === "community";
  const showLeadership = filter === "all" || filter === "leadership";

  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pb-10 pt-32 md:px-12 md:pt-40">
        <p className="eyebrow">Career Record</p>
        <h1 className="mt-3 font-serif text-4xl font-medium leading-tight text-teal-900 md:text-5xl">
          Experience &amp; Programmes
        </h1>
        <p className="mt-5 max-w-[52ch] text-[0.95rem] leading-relaxed text-ink-soft">
          Roles, cultural programmes, research and innovation from more than two decades in public service.
        </p>
      </div>

      <div className="sticky top-[65px] z-30 border-y border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-content gap-8 overflow-x-auto px-6 md:px-12">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`whitespace-nowrap border-b-2 py-4 text-[0.72rem] uppercase tracking-widest2 transition-colors ${
                filter === t.key
                  ? "border-teal-900 text-teal-900"
                  : "border-transparent text-ink-faint hover:text-ink-soft"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {showClinical && (
        <section className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
          <p className="eyebrow">Career</p>
          <h2 className="mt-2 font-serif text-2xl font-medium text-teal-900 md:text-3xl">
            Roles &amp; Responsibilities
          </h2>
          <div className="mt-10 divide-y divide-line border-t border-line">
            {careerHighlights.map((p, i) => (
              <Reveal key={p.title} delay={i * 60} className="grid gap-2 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
                <div>
                  <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-500">{p.tag}</p>
                  <p className="mt-2 text-[0.72rem] uppercase tracking-widest2 text-ink-faint">{p.foot}</p>
                </div>
                <div>
                  <h3 className="font-serif text-xl text-teal-900">{p.title}</h3>
                  <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {showCommunity && (
        <section className="border-t border-line">
          <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
            <p className="eyebrow">Cultural Development</p>
            <h2 className="mt-2 font-serif text-2xl font-medium text-teal-900 md:text-3xl">
              Programmes I Have Coordinated
            </h2>
            <div className="mt-10 divide-y divide-line border-t border-line">
              {culturalProgrammes.map((p, i) => (
                <Reveal key={p.title} delay={i * 60} className="grid gap-2 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
                  <div>
                    <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-500">{p.tag}</p>
                    <p className="mt-2 text-[0.72rem] uppercase tracking-widest2 text-ink-faint">{p.foot}</p>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-teal-900">{p.title}</h3>
                    <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <p className="eyebrow mt-16">Leadership</p>
            <h2 className="mt-2 font-serif text-2xl font-medium text-teal-900 md:text-3xl">Union &amp; Administrative Positions</h2>
            <div className="mt-10 grid gap-px border border-line bg-line sm:grid-cols-2">
              {leadershipRoles.map((r, i) => (
                <Reveal key={r.role} delay={i * 60} className="bg-white p-7">
                  <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-500">{r.org}</p>
                  <h3 className="mt-2 font-serif text-lg text-teal-900">{r.role}</h3>
                  <p className="mt-2 text-[0.9rem] leading-relaxed text-ink-soft">{r.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {showLeadership && (
        <section className="border-t border-line bg-stone-50">
          <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-20">
            <p className="eyebrow">Research &amp; Innovation</p>
            <h2 className="mt-2 font-serif text-2xl font-medium text-teal-900 md:text-3xl">
              Research &amp; Innovation
            </h2>
            <div className="mt-10 divide-y divide-line border-t border-line">
              {leadershipInitiatives.map((p) => (
                <Reveal key={p.title} className="grid gap-2 py-8 md:grid-cols-[1fr_2fr] md:gap-10">
                  <div>
                    <p className="text-[0.68rem] uppercase tracking-widest2 text-teal-500">{p.tag}</p>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-teal-900">{p.title}</h3>
                    <p className="mt-2 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">{p.desc}</p>
                    {p.detailPath && (
                      <Link
                        to={p.detailPath}
                        className="mt-4 inline-block text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900"
                      >
                        Read the full study &rarr;
                      </Link>
                    )}
                    {p.href && (
                      <a
                        href={p.href}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-block text-[0.72rem] uppercase tracking-widest2 text-teal-700 hover:text-teal-900"
                      >
                        {p.linkLabel} &rarr;
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
